import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, FileText, Loader2, Trash2, Download, CheckCircle2, AlertTriangle, Clock, ListChecks, Circle } from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import { useUser } from "../../context/UserContext";
import {
  fetchMyDocuments, uploadDocument, deleteDocument, documentDownloadUrl, fetchRequirements, formatDate,
} from "../../lib/api";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.docx,.xlsx";
const MAX_MB = 10;

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const statusMeta = {
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-700", icon: Clock },
  reviewed: { label: "Reviewed", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  attention: { label: "Needs attention", cls: "bg-red-100 text-red-600", icon: AlertTriangle },
};

export default function PortalDocuments() {
  const { token } = useUser();
  const [docs, setDocs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const pendingReqRef = useRef(null);

  const refresh = () => {
    if (!token) return;
    fetchMyDocuments(token).then(setDocs).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    refresh();
    if (token) fetchRequirements(token).then(setReqs).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = async (fileList) => {
    const file = fileList?.[0];
    const reqId = pendingReqRef.current;
    pendingReqRef.current = null;
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`"${file.name}" is larger than ${MAX_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      await uploadDocument(token, file, reqId);
      toast.success("Document uploaded for review");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const pickFor = (reqId) => {
    pendingReqRef.current = reqId;
    inputRef.current?.click();
  };

  const providedReqIds = new Set(docs.map((d) => d.requirement_id).filter(Boolean));

  const confirmDelete = async () => {
    try {
      await deleteDocument(token, deleteId);
      setDeleteId(null);
      refresh();
      toast.success("Document removed");
    } catch (e) {
      toast.error("Could not remove document");
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-documents">
      <div>
        <h1 className="font-serif text-2xl font-bold text-navy">My Documents</h1>
        <p className="text-gray-500 text-sm mt-0.5">Upload documents for your coach to review securely.</p>
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          dragOver ? "border-gold bg-gold/5" : "border-gray-200 bg-white hover:border-navy/30"
        }`}
        data-testid="document-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="document-file-input"
        />
        <div className="mx-auto h-12 w-12 rounded-full bg-cream flex items-center justify-center">
          {uploading ? <Loader2 className="h-6 w-6 text-gold-dark animate-spin" /> : <UploadCloud className="h-6 w-6 text-gold-dark" />}
        </div>
        <p className="mt-3 font-medium text-navy">{uploading ? "Uploading…" : "Click to upload or drag & drop"}</p>
        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, DOCX, XLSX · up to {MAX_MB}MB</p>
      </div>

      {/* Required documents checklist */}
      {reqs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="requirements-checklist">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="h-5 w-5 text-gold-dark" />
            <h2 className="font-serif text-lg font-semibold text-navy">Documents we need from you</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {reqs.map((r) => {
              const done = providedReqIds.has(r.id);
              return (
                <div key={r.id} className="flex items-center gap-3 py-3" data-testid={`checklist-${r.id}`}>
                  {done ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-300 flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium ${done ? "text-gray-400 line-through" : "text-navy"}`}>{r.label}</div>
                    {r.description && <div className="text-xs text-gray-400">{r.description}</div>}
                  </div>
                  {done ? (
                    <span className="text-xs font-medium text-green-600">Provided</span>
                  ) : (
                    <Button
                      onClick={() => pickFor(r.id)}
                      disabled={uploading}
                      variant="outline"
                      className="h-8 text-sm border-navy/20 text-navy hover:bg-navy hover:text-white"
                      data-testid={`checklist-upload-${r.id}`}
                    >
                      <UploadCloud className="h-4 w-4 mr-1.5" /> Upload
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Document list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      ) : docs.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center text-gray-400">
          <FileText className="h-10 w-10 mx-auto mb-3" />
          You haven't uploaded any documents yet.
        </div>
      ) : (
        <div className="grid gap-3">
          {docs.map((d) => {
            const meta = statusMeta[d.status] || statusMeta.pending;
            const SIcon = meta.icon;
            return (
              <div key={d.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={`document-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-cream flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-gold-dark" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-navy truncate">{d.original_filename}</div>
                    <div className="text-xs text-gray-400">{fmtSize(d.size)} · {formatDate(d.created_at)}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${meta.cls}`}>
                    <SIcon className="h-3.5 w-3.5" /> {meta.label}
                  </span>
                </div>
                {d.admin_note && (
                  <div className="mt-3 bg-cream rounded-lg p-3 text-sm text-gray-600">
                    <span className="font-medium text-navy">Coach's note: </span>{d.admin_note}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <a
                    href={documentDownloadUrl(token, d.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-navy hover:text-gold-dark font-medium"
                    data-testid={`document-download-${d.id}`}
                  >
                    <Download className="h-4 w-4" /> View / Download
                  </a>
                  <button
                    onClick={() => setDeleteId(d.id)}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-500"
                    data-testid={`document-delete-${d.id}`}
                  >
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this document?</AlertDialogTitle>
            <AlertDialogDescription>This removes the file from your portal. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
